import { Fragment } from 'react';
import Link from 'next/link';
import styles from './interior.module.css';

export interface BreadcrumbCrumb {
  readonly label: string;
  /** Omit on the last crumb: the current page is never a link. */
  readonly href?: string;
  /**
   * Overrides the `aria-current="page"` that an href-less crumb gets by
   * default. `/learn/[slug]` needs `false`: its trail terminates on the
   * concept's CATEGORY, while the `<h1>` and its `BreadcrumbSchema` both
   * terminate on the concept title, so claiming the category is the page
   * contradicts two surfaces that already agree with each other.
   */
  readonly current?: boolean;
}

interface BreadcrumbProps {
  /** Root first, current page last: `Home / Learn / <title>`. */
  readonly items: readonly BreadcrumbCrumb[];
}

/**
 * The interior-page breadcrumb trail. It belongs directly above the `<h1>`:
 * `interiorStyles.breadcrumb` carries a `margin-bottom` and no top spacing, so
 * a copy placed after the page body has nothing to bind to and reads as a
 * stray line floating above the footer.
 *
 * Every visible trail on the landing site renders through this component, and
 * `breadcrumb-component.guard.spec.ts` fails a page that hand-rolls one. The
 * guard bans the divergent markup rather than the misplacement, because
 * divergence is what let one page's placement drift unnoticed.
 *
 * `BreadcrumbSchema` is unrelated and out of scope for that guard -- it emits
 * JSON-LD and renders no nav.
 */
export function Breadcrumb({ items }: BreadcrumbProps): React.ReactElement {
  return (
    <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
      <ol>
        {items.map((item, index) => (
          <Fragment key={`${index}-${item.label}`}>
            {index > 0 && <li aria-hidden="true">/</li>}
            <li aria-current={(item.current ?? !item.href) ? 'page' : undefined}>
              {item.href ? <Link href={item.href}>{item.label}</Link> : item.label}
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  );
}
